// Notifications — deuxième écran migré.
//
// Premier écran de la migration qui s'abonne à la base : il éprouve
// `useAbonnement`, et donc tout le reste de ce qui viendra après.

import Store from "../../store.js";
import { reaction, reactionSure } from "../../reactions.js";
import { ilYA } from "../../ui.js";
import Avatar from "../Avatar.jsx";
import Certifie from "../Certifie.jsx";
import Icone from "../Icone.jsx";
import Vide from "../Vide.jsx";
import { useAbonnement, useApresUnInstant, useMembre } from "../hooks.js";

// Ce qui SUIT le nom. Le nom lui-même est rendu à part, pour que le sceau de
// certification puisse se glisser juste après lui — comme sur Facebook, où la
// coche colle au nom et non à la fin de la phrase.
const TEXTES = {
  amen: "a soutenu votre publication par un Amen",
  priere: "prie pour votre demande",
  commentaire: "a commenté votre publication",
  partage: "a partagé votre publication",
  story: "a publié un nouveau statut",
  mention: "vous a identifié",
  demande_ami: "souhaite vous ajouter en ami",
  ami_accepte: "a accepté votre demande d'amitié",
  // Deux mots suffisent. « S'est abonné à vos publications » décrivait le
  // mécanisme ; ce qu'on veut lire, c'est ce qui est arrivé.
  abonne: "vous suit",
};

/**
 * La pastille posée sur l'avatar — un disque de couleur, un tracé blanc.
 *
 * ⚠️ PLUS D'ÉMOJI. Ils étaient commodes et ils étaient faux : chaque système
 * dessine les siens, si bien que 🙏 est une paire de mains sur un téléphone et
 * deux mains qui se tapent sur un autre, 🎞️ une pellicule ici et un ruban
 * ailleurs. Rien de tout cela ne ressemblait à Koinonia, et rien n'était
 * identique d'un appareil à l'autre. Les tracés de `icones.js` sont les nôtres,
 * et ils sont les mêmes partout.
 *
 * La COULEUR fait le premier tri, avant même qu'on ait lu : on reconnaît le
 * vert d'un commentaire du coin de l'œil. Elle est donc portée par la FAMILLE
 * du geste, pas par le type — deux notifications de même couleur veulent dire
 * deux choses du même ordre.
 *
 *   bleu      le soutien — l'Amen, la prière (demandé par Josaphat,
 *             24 octobre 2026 : « soutenir votre pub doit être en bleu ») —
 *             et les gens, l'amitié dans les deux sens
 *   vert      la parole — les commentaires
 *   turquoise l'audience — un abonné de plus
 *   violet    ce qui circule — les partages, les mentions
 *   rose      l'éphémère — les statuts
 *
 * ⚠️ Et quand l'alerte porte SA réaction (`n.reaction`), la pastille prend la
 * COULEUR DE LA RÉACTION — voir `couleur` dans js/reactions.js.
 */
const PASTILLES = {
  amen: { icone: "amenPastille", couleur: "bleu" },
  priere: { icone: "amenPastille", couleur: "bleu" },
  commentaire: { icone: "commenter", couleur: "vert" },
  partage: { icone: "partager", couleur: "violet" },
  mention: { icone: "etiquette", couleur: "violet" },
  story: { icone: "reels", couleur: "rose" },
  demande_ami: { icone: "amiPlus", couleur: "bleu" },
  ami_accepte: { icone: "amisDeux", couleur: "bleu" },
  abonne: { icone: "amiCoche", couleur: "turquoise" },
};

const PASTILLE_DEFAUT = { icone: "alertes", couleur: "bleu" };

/**
 * Les messages privés ne viennent PLUS ici.
 *
 * Une notification « untel vous a envoyé un message » n'apprenait rien : le
 * message lui-même est déjà la nouvelle, et il attend dans la messagerie. Elle
 * ne faisait que doubler chaque conversation et noyer ce qui a vraiment besoin
 * d'être remarqué — un Amen, une prière, une demande d'amitié. Le compte des
 * conversations non lues s'affiche sur l'icône Messages, et l'appareil prévient
 * depuis `app.js`, qui suit les conversations directement.
 *
 * On filtre au lieu d'attendre que les anciennes disparaissent : celles écrites
 * avant ce changement sont encore dans la base des membres.
 */
const AFFICHABLE = (n) => n.type !== "message";

/** Où mène une notification quand on la touche. */
function lien(n) {
  if (n.type === "demande_ami") return "#/membres";
  if (n.type === "ami_accepte" || n.type === "abonne") return `#/profil/${n.deId}`;
  return n.postId ? `#/publication/${n.postId}` : "#/fil";
}

function Ligne({ n, ctx }) {
  const suite = TEXTES[n.type] || "a interagi avec vous";
  const pastille = PASTILLES[n.type] || PASTILLE_DEFAUT;
  // Une alerte de soutien qui porte SA réaction prend la couleur de celle-ci
  // (js/reactions.js) ; les anciennes alertes, sans le champ, gardent le bleu.
  const teinte = (n.type === "amen" || n.type === "priere") && reactionSure(n.reaction)
    ? reaction(n.reaction).couleur
    : null;
  const de = useMembre(ctx, n.deId, { nom: n.deNom, photo: n.dePhoto });
  return (
    <a className={`notif${n.lu ? "" : " est-non-lu"}`} href={lien(n)}>
      <span className="notif__avatar">
        <Avatar membre={de} taille={42} />
        <span
          className={`notif__pastille notif__pastille--${pastille.couleur}`}
          style={teinte ? { background: teinte } : undefined}
        >
          <Icone nom={pastille.icone} taille={12} plein />
        </span>
      </span>
      <span className="notif__texte">
        <span>
          <strong>{de.nom}</strong>
          <Certifie membre={de} taille={13} /> {suite}
        </span>
        {n.apercu && <span className="notif__apercu">{n.apercu}</span>}
        <span className="petit texte-doux">{ilYA(n.createdAt)}</span>
      </span>
      {!n.lu && <span className="pastille" />}
    </a>
  );
}

export default function Notifications({ ctx }) {
  const toutes = useAbonnement((cb) => Store.notifs.subscribe(cb), []);
  const notifs = toutes.filter(AFFICHABLE);

  // Ouvrir la page vaut lecture — mais pas à l'instant même, sinon la pastille
  // disparaîtrait avant que l'œil ait eu le temps de la voir.
  useApresUnInstant(() => Store.notifs.toutLire().catch(() => {}));

  return (
    <div className="colonne">
      <div className="entete-page">
        <h1 className="titre-page">Notifications</h1>
        <button
          className="btn btn--fantome btn--petit"
          onClick={() => Store.notifs.toutLire()}
        >
          Tout marquer comme lu
        </button>
      </div>

      {notifs.length ? (
        <div className="carte carte--liste">
          {notifs.map((n) => (
            <Ligne key={n.id} n={n} ctx={ctx} />
          ))}
        </div>
      ) : (
        <Vide
          symbole={<Icone nom="alertes" taille={44} />}
          titre="Aucune notification"
          texte="Vous serez prévenu quand un membre réagit à vos publications."
        />
      )}
    </div>
  );
}
